import type { BomHardwareLine, BomWoodLine } from '@/services/boms/types'

export type ModifiedLine<T> = {
  current: T
  previous: T
  diffs: string[]
}

export type CategoryComparison<T> = {
  added: T[]
  removed: T[]
  modified: ModifiedLine<T>[]
  unchanged: T[]
}

export type BomComparisonResult = {
  wood: CategoryComparison<BomWoodLine>
  hardware: CategoryComparison<BomHardwareLine>
}

function normalizeKey(str: string | null | undefined): string {
  return (str ?? '').trim().toLowerCase()
}

export function compareBomVersions(
  current: { woodLines: BomWoodLine[]; hardwareLines: BomHardwareLine[] },
  previous: { woodLines: BomWoodLine[]; hardwareLines: BomHardwareLine[] },
): BomComparisonResult {
  // 1. Compare Wood Lines
  const woodAdded: BomWoodLine[] = []
  const woodRemoved: BomWoodLine[] = []
  const woodModified: ModifiedLine<BomWoodLine>[] = []
  const woodUnchanged: BomWoodLine[] = []

  const prevWoodMatched = new Set<number>()

  for (const curLine of current.woodLines) {
    const curKey = normalizeKey(curLine.part_name)
    const prevIdx = previous.woodLines.findIndex((p, idx) => {
      if (prevWoodMatched.has(idx)) return false
      if (curLine.id && p.id && curLine.id === p.id) return true
      return normalizeKey(p.part_name) === curKey
    })

    if (prevIdx === -1) {
      woodAdded.push(curLine)
    } else {
      prevWoodMatched.add(prevIdx)
      const prevLine = previous.woodLines[prevIdx]
      const diffs: string[] = []

      if (curLine.dims_finished_t !== prevLine.dims_finished_t) diffs.push('Dày')
      if (curLine.dims_finished_w !== prevLine.dims_finished_w) diffs.push('Rộng')
      if (curLine.dims_finished_l !== prevLine.dims_finished_l) diffs.push('Dài')
      if (curLine.tenon !== prevLine.tenon) diffs.push('Mộng')
      if (curLine.qty_per !== prevLine.qty_per) diffs.push('Số lượng')
      if (curLine.wood_type_raw !== prevLine.wood_type_raw) diffs.push('Loại gỗ')
      if (curLine.veneer_spec !== prevLine.veneer_spec) diffs.push('Veneer')
      if (curLine.notes !== prevLine.notes) diffs.push('Ghi chú')

      if (diffs.length > 0) {
        woodModified.push({ current: curLine, previous: prevLine, diffs })
      } else {
        woodUnchanged.push(curLine)
      }
    }
  }

  previous.woodLines.forEach((p, idx) => {
    if (!prevWoodMatched.has(idx)) {
      woodRemoved.push(p)
    }
  })

  // 2. Compare Hardware Lines
  const hwAdded: BomHardwareLine[] = []
  const hwRemoved: BomHardwareLine[] = []
  const hwModified: ModifiedLine<BomHardwareLine>[] = []
  const hwUnchanged: BomHardwareLine[] = []

  const prevHwMatched = new Set<number>()

  for (const curLine of current.hardwareLines) {
    const curKey = normalizeKey(curLine.name_raw)
    const prevIdx = previous.hardwareLines.findIndex((p, idx) => {
      if (prevHwMatched.has(idx)) return false
      if (curLine.id && p.id && curLine.id === p.id) return true
      return normalizeKey(p.name_raw) === curKey
    })

    if (prevIdx === -1) {
      hwAdded.push(curLine)
    } else {
      prevHwMatched.add(prevIdx)
      const prevLine = previous.hardwareLines[prevIdx]
      const diffs: string[] = []

      if (curLine.qty_per !== prevLine.qty_per) diffs.push('Số lượng')
      if (curLine.spec_raw !== prevLine.spec_raw) diffs.push('Quy cách')
      if (curLine.uom_hint !== prevLine.uom_hint) diffs.push('ĐVT')
      if (curLine.color_hint !== prevLine.color_hint) diffs.push('Màu sắc')
      if (curLine.notes !== prevLine.notes) diffs.push('Ghi chú')

      if (diffs.length > 0) {
        hwModified.push({ current: curLine, previous: prevLine, diffs })
      } else {
        hwUnchanged.push(curLine)
      }
    }
  }

  previous.hardwareLines.forEach((p, idx) => {
    if (!prevHwMatched.has(idx)) {
      hwRemoved.push(p)
    }
  })

  return {
    wood: {
      added: woodAdded,
      removed: woodRemoved,
      modified: woodModified,
      unchanged: woodUnchanged,
    },
    hardware: {
      added: hwAdded,
      removed: hwRemoved,
      modified: hwModified,
      unchanged: hwUnchanged,
    },
  }
}
